import { useState, useEffect, useCallback, useRef } from "react";

const AudioCtx = window.AudioContext || window.webkitAudioContext;

/* ====== SOUND EFFECTS ====== */
const SOUND_DEFS = {
  click: { freq: 800, duration: 0.05, type: "sine", volume: 0.15 },
  hover: { freq: 600, duration: 0.03, type: "sine", volume: 0.06 },
  success: { freqs: [523, 659, 784], duration: 0.15, type: "sine", volume: 0.2 },
  error: { freqs: [330, 250], duration: 0.15, type: "square", volume: 0.12 },
  star: { freqs: [880, 1047, 1319], duration: 0.12, type: "sine", volume: 0.18 },
  levelUp: { freqs: [523, 659, 784, 1047], duration: 0.2, type: "sine", volume: 0.2 },
  notification: { freqs: [659, 784], duration: 0.1, type: "triangle", volume: 0.15 },
  pop: { freq: 1200, duration: 0.04, type: "sine", volume: 0.1 },
  catch: { freqs: [784, 988, 1175], duration: 0.1, type: "sine", volume: 0.2 },
  reward: { freqs: [523, 659, 784, 988, 1175], duration: 0.15, type: "sine", volume: 0.22 },
};

/* ====== BEAT ENGINE, Real Hip-Hop/Pop/Electronic Beats ====== */
/* Each track has: bpm, drum pattern, bass line, melody, chord pads */

function createKick(ctx, time, vol) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(150, time);
  osc.frequency.exponentialRampToValueAtTime(30, time + 0.12);
  gain.gain.setValueAtTime(vol * 0.7, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.2);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(time);
  osc.stop(time + 0.25);
  return osc;
}

function createSnare(ctx, time, vol) {
  // Noise burst for snare
  const bufferSize = ctx.sampleRate * 0.08;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.8;
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(vol * 0.3, time);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, time + 0.1);
  // Add a tone body
  const osc = ctx.createOscillator();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(180, time);
  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(vol * 0.15, time);
  oscGain.gain.exponentialRampToValueAtTime(0.001, time + 0.06);
  noise.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  osc.connect(oscGain);
  oscGain.connect(ctx.destination);
  noise.start(time);
  noise.stop(time + 0.12);
  osc.start(time);
  osc.stop(time + 0.1);
  return noise;
}

function createHihat(ctx, time, vol, open = false) {
  const bufferSize = ctx.sampleRate * (open ? 0.15 : 0.03);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  // High-pass filter for metallic sound
  const filter = ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.setValueAtTime(8000, time);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(vol * 0.12, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + (open ? 0.18 : 0.04));
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  noise.start(time);
  noise.stop(time + (open ? 0.2 : 0.06));
  return noise;
}

function createBass(ctx, time, freq, dur, vol) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(freq, time);
  // Low-pass filter for warm bass
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(300, time);
  filter.Q.setValueAtTime(5, time);
  gain.gain.setValueAtTime(vol * 0.18, time);
  gain.gain.setValueAtTime(vol * 0.15, time + dur * 0.7);
  gain.gain.exponentialRampToValueAtTime(0.001, time + dur);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  osc.start(time);
  osc.stop(time + dur + 0.01);
  return osc;
}

function createMelody(ctx, time, freq, dur, vol, waveform = "square") {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = waveform;
  osc.frequency.setValueAtTime(freq, time);
  // Slight pitch vibrato
  const lfo = ctx.createOscillator();
  const lfoGain = ctx.createGain();
  lfo.frequency.setValueAtTime(5, time);
  lfoGain.gain.setValueAtTime(2, time);
  lfo.connect(lfoGain);
  lfoGain.connect(osc.frequency);
  // Filter for warmth
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(2000, time);
  gain.gain.setValueAtTime(vol * 0.06, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + dur * 0.95);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  osc.start(time);
  osc.stop(time + dur + 0.01);
  lfo.start(time);
  lfo.stop(time + dur + 0.01);
  return osc;
}

function createPad(ctx, time, freqs, dur, vol) {
  const nodes = freqs.map(freq => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, time);
    gain.gain.setValueAtTime(vol * 0.025, time);
    gain.gain.setValueAtTime(vol * 0.02, time + dur * 0.8);
    gain.gain.exponentialRampToValueAtTime(0.001, time + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(time);
    osc.stop(time + dur + 0.01);
    return osc;
  });
  return nodes;
}

/* Track definitions, real beat patterns */
const TRACKS = {
  "hip-hop-vibes": {
    name: "Hip-Hop Vibes", bpm: 90, bars: 4, beatsPerBar: 4,
    // K=kick, S=snare, H=hihat, O=open hihat, .=rest
    drums: [
      "K.H.S.H.", "K.H.S.HK", "K.H.S.H.", "K.H.S.HO",
    ],
    bass: [
      [55, 55, 0, 65.41], [55, 0, 73.42, 65.41],
      [55, 55, 0, 65.41], [55, 0, 73.42, 0],
    ],
    melody: [
      [330, 0, 392, 349], [330, 294, 0, 349],
      [330, 0, 392, 440], [392, 349, 330, 0],
    ],
    pad: [[220, 277, 330], [196, 247, 294], [220, 277, 330], [175, 220, 262]],
    melodyWave: "square",
  },
  "pop-bounce": {
    name: "Pop Bounce", bpm: 110, bars: 4, beatsPerBar: 4,
    drums: [
      "KHH.SHHK", "HH.KSHH.", "KHH.SHHK", "HH.KSHHO",
    ],
    bass: [
      [130.81, 0, 146.83, 130.81], [110, 0, 130.81, 0],
      [130.81, 0, 146.83, 130.81], [110, 0, 123.47, 130.81],
    ],
    melody: [
      [523, 587, 659, 0], [587, 523, 0, 494],
      [523, 587, 659, 784], [659, 587, 523, 0],
    ],
    pad: [[262, 330, 392], [220, 277, 330], [262, 330, 392], [247, 311, 370]],
    melodyWave: "triangle",
  },
  "trap-energy": {
    name: "Trap Energy", bpm: 140, bars: 4, beatsPerBar: 4,
    drums: [
      "K..KS..K", "..K.S.KS", "K..KS..K", "..KKS.SO",
    ],
    bass: [
      [55, 0, 0, 55], [0, 0, 55, 0],
      [55, 0, 0, 55], [0, 55, 0, 0],
    ],
    melody: [
      [440, 0, 523, 0], [494, 0, 440, 0],
      [440, 0, 523, 587], [494, 440, 0, 0],
    ],
    pad: [[220, 262, 330], [220, 262, 330], [196, 247, 294], [196, 247, 294]],
    melodyWave: "sawtooth",
  },
  "edm-drop": {
    name: "EDM Drop", bpm: 128, bars: 4, beatsPerBar: 4,
    drums: [
      "KHKHSKHK", "HKHKSKHO", "KHKHSKHK", "HKHKSKHK",
    ],
    bass: [
      [65.41, 65.41, 0, 73.42], [82.41, 0, 73.42, 65.41],
      [65.41, 65.41, 0, 73.42], [82.41, 73.42, 65.41, 0],
    ],
    melody: [
      [659, 784, 880, 784], [659, 587, 659, 0],
      [659, 784, 880, 988], [880, 784, 659, 587],
    ],
    pad: [[330, 415, 494], [294, 370, 440], [330, 415, 494], [262, 330, 392]],
    melodyWave: "square",
  },
  "chill-groove": {
    name: "Chill Groove", bpm: 85, bars: 4, beatsPerBar: 4,
    drums: [
      "K.H.S.H.", ".KH.S.HO", "K.H.S.H.", ".KH.S.H.",
    ],
    bass: [
      [82.41, 0, 0, 73.42], [0, 82.41, 0, 0],
      [82.41, 0, 0, 73.42], [65.41, 0, 73.42, 0],
    ],
    melody: [
      [392, 0, 440, 0], [494, 0, 440, 392],
      [392, 0, 440, 494], [523, 0, 494, 0],
    ],
    pad: [[196, 247, 294], [220, 277, 330], [196, 247, 294], [175, 220, 262]],
    melodyWave: "sine",
  },
  "funk-party": {
    name: "Funk Party", bpm: 115, bars: 4, beatsPerBar: 4,
    drums: [
      "KHSHKHSH", "KHSHKHSO", "KHSHKHSH", "KSKHKHSH",
    ],
    bass: [
      [98, 0, 110, 98], [82.41, 0, 98, 0],
      [98, 0, 110, 98], [82.41, 98, 0, 110],
    ],
    melody: [
      [392, 440, 0, 523], [494, 0, 440, 392],
      [392, 440, 494, 523], [587, 523, 494, 0],
    ],
    pad: [[196, 247, 294], [220, 277, 330], [196, 247, 294], [175, 220, 262]],
    melodyWave: "triangle",
  },
  /* ====== WORLD AMBIENT SOUNDSCAPES ====== */
  "enchanted-forest": {
    name: "Enchanted Forest", bpm: 68, bars: 4, beatsPerBar: 4,
    drums: ["..H.....", "........", "..H.....", "........"],
    bass: [[82.41, 0, 0, 0], [73.42, 0, 0, 0], [82.41, 0, 0, 0], [65.41, 0, 0, 0]],
    melody: [[392, 0, 494, 0], [440, 0, 0, 523], [392, 0, 494, 587], [523, 0, 440, 0]],
    pad: [[196, 247, 330, 392], [220, 277, 349, 440], [196, 247, 330, 392], [175, 220, 294, 349]],
    melodyWave: "sine",
  },
  "neon-rush": {
    name: "Neon Rush", bpm: 126, bars: 4, beatsPerBar: 4,
    drums: ["KHKHSKHK", "KHKHSKHO", "KHKHSKHK", "KKSHKHSK"],
    bass: [[55, 55, 0, 65.41], [55, 0, 73.42, 55], [55, 55, 0, 65.41], [82.41, 73.42, 55, 0]],
    melody: [[659, 784, 0, 880], [784, 0, 659, 0], [659, 784, 880, 988], [880, 0, 784, 659]],
    pad: [[220, 330, 440], [247, 370, 494], [220, 330, 440], [196, 294, 392]],
    melodyWave: "square",
  },
  "ocean-voyage": {
    name: "Ocean Voyage", bpm: 76, bars: 4, beatsPerBar: 4,
    drums: ["K.......S.......", "........K..H....", "K.......S.......", "....K.......S..."],
    bass: [[73.42, 0, 0, 0], [82.41, 0, 0, 0], [73.42, 0, 0, 0], [65.41, 0, 73.42, 0]],
    melody: [[349, 0, 440, 523], [494, 0, 440, 0], [349, 0, 440, 587], [523, 0, 440, 349]],
    pad: [[175, 220, 294, 349], [196, 247, 330, 392], [175, 220, 294, 349], [164, 220, 262, 330]],
    melodyWave: "triangle",
  },
  "creative-canvas": {
    name: "Creative Canvas", bpm: 82, bars: 4, beatsPerBar: 4,
    drums: ["..H.....H.......", "..H.....H.......", "..H.....H..H....", "..H.....H......."],
    bass: [[98, 0, 0, 0], [87.31, 0, 0, 0], [98, 0, 0, 0], [82.41, 0, 87.31, 0]],
    melody: [[523, 0, 587, 0], [659, 0, 587, 523], [523, 0, 659, 784], [659, 587, 523, 0]],
    pad: [[262, 330, 392], [247, 311, 370], [262, 330, 392], [220, 277, 330]],
    melodyWave: "sine",
  },
  "cosmic-explorer": {
    name: "Cosmic Explorer", bpm: 72, bars: 4, beatsPerBar: 4,
    drums: ["........", "..H.....", "........", "..H....."],
    bass: [[55, 0, 0, 0], [65.41, 0, 0, 0], [55, 0, 0, 0], [49, 0, 55, 0]],
    melody: [[440, 0, 523, 0], [587, 0, 0, 523], [440, 0, 587, 659], [523, 0, 440, 0]],
    pad: [[220, 277, 349, 440], [247, 311, 392, 494], [220, 277, 349, 440], [196, 247, 330, 392]],
    melodyWave: "sine",
  },
};

export function useSounds() {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem("isaiah_sound_enabled");
    return saved !== null ? saved === "true" : true;
  });
  const [musicEnabled, setMusicEnabled] = useState(() => {
    const saved = localStorage.getItem("isaiah_music_enabled");
    return saved !== null ? saved === "true" : false;
  });
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem("isaiah_volume");
    return saved !== null ? parseFloat(saved) : 0.5;
  });
  const [currentTrack, setCurrentTrack] = useState(null);

  const ctxRef = useRef(null);
  const schedulerRef = useRef(null);
  const currentTrackRef = useRef(null);
  const nodesRef = useRef([]);

  const getCtx = useCallback(() => {
    if (!ctxRef.current || ctxRef.current.state === "closed") {
      ctxRef.current = new AudioCtx();
    }
    if (ctxRef.current.state === "suspended") {
      ctxRef.current.resume();
    }
    return ctxRef.current;
  }, []);

  useEffect(() => { localStorage.setItem("isaiah_sound_enabled", soundEnabled); }, [soundEnabled]);
  useEffect(() => { localStorage.setItem("isaiah_music_enabled", musicEnabled); }, [musicEnabled]);
  useEffect(() => { localStorage.setItem("isaiah_volume", volume); }, [volume]);

  const playTone = useCallback((freq, duration, type, vol) => {
    if (!soundEnabled) return;
    try {
      const ctx = getCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(vol * volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + duration + 0.01);
    } catch (e) {}
  }, [soundEnabled, volume, getCtx]);

  const playSound = useCallback((name) => {
    if (!soundEnabled) return;
    const def = SOUND_DEFS[name];
    if (!def) return;
    if (def.freqs) {
      def.freqs.forEach((f, i) => {
        setTimeout(() => playTone(f, def.duration, def.type, def.volume), i * (def.duration * 500));
      });
    } else {
      playTone(def.freq, def.duration, def.type, def.volume);
    }
  }, [soundEnabled, playTone]);

  const stopMusic = useCallback(() => {
    if (schedulerRef.current) {
      clearInterval(schedulerRef.current);
      schedulerRef.current = null;
    }
    nodesRef.current.forEach(n => { try { n.stop(); } catch (e) {} });
    nodesRef.current = [];
    currentTrackRef.current = null;
    setCurrentTrack(null);
  }, []);

  const playMusic = useCallback((trackName) => {
    if (!musicEnabled) return;
    const track = TRACKS[trackName];
    if (!track) return;
    if (currentTrackRef.current === trackName) return;
    stopMusic();
    currentTrackRef.current = trackName;
    setCurrentTrack(trackName);

    const ctx = getCtx();
    const secPerBeat = 60 / track.bpm;
    let currentBar = 0;

    const scheduleBar = () => {
      if (!musicEnabled || currentTrackRef.current !== trackName) return;
      try {
        const bar = currentBar % track.bars;
        const barStart = ctx.currentTime + 0.05;
        const drumPattern = track.drums[bar];
        const vol = volume;

        // Schedule drums
        for (let beat = 0; beat < drumPattern.length; beat++) {
          const t = barStart + beat * (secPerBeat / 2);
          const ch = drumPattern[beat];
          if (ch === "K") nodesRef.current.push(createKick(ctx, t, vol));
          if (ch === "S") nodesRef.current.push(createSnare(ctx, t, vol));
          if (ch === "H") nodesRef.current.push(createHihat(ctx, t, vol));
          if (ch === "O") nodesRef.current.push(createHihat(ctx, t, vol, true));
        }

        // Schedule bass
        const bassNotes = track.bass[bar];
        for (let i = 0; i < bassNotes.length; i++) {
          if (bassNotes[i] > 0) {
            const t = barStart + i * secPerBeat;
            nodesRef.current.push(createBass(ctx, t, bassNotes[i], secPerBeat * 0.9, vol));
          }
        }

        // Schedule melody
        const melodyNotes = track.melody[bar];
        for (let i = 0; i < melodyNotes.length; i++) {
          if (melodyNotes[i] > 0) {
            const t = barStart + i * secPerBeat;
            nodesRef.current.push(createMelody(ctx, t, melodyNotes[i], secPerBeat * 0.8, vol, track.melodyWave));
          }
        }

        // Schedule pad (chord sustained across whole bar)
        const padChord = track.pad[bar];
        if (padChord) {
          const padNodes = createPad(ctx, barStart, padChord, secPerBeat * 3.8, vol);
          nodesRef.current.push(...padNodes);
        }

        currentBar++;

        // Cleanup old references
        if (nodesRef.current.length > 100) {
          nodesRef.current = nodesRef.current.slice(-30);
        }
      } catch (e) {}
    };

    scheduleBar();
    const barDuration = secPerBeat * track.beatsPerBar * 1000;
    schedulerRef.current = setInterval(scheduleBar, barDuration);
  }, [musicEnabled, volume, getCtx, stopMusic]);

  useEffect(() => { if (!musicEnabled) stopMusic(); }, [musicEnabled, stopMusic]);
  useEffect(() => () => stopMusic(), [stopMusic]);

  const toggleSound = useCallback(() => setSoundEnabled(prev => !prev), []);
  const toggleMusic = useCallback(() => setMusicEnabled(prev => !prev), []);

  const trackList = Object.keys(TRACKS);

  const nextTrack = useCallback(() => {
    const idx = trackList.indexOf(currentTrackRef.current || "");
    const next = trackList[(idx + 1) % trackList.length];
    stopMusic();
    if (musicEnabled) {
      setTimeout(() => playMusic(next), 100);
    }
  }, [musicEnabled, playMusic, stopMusic, trackList]);

  const trackNames = Object.fromEntries(Object.entries(TRACKS).map(([k, v]) => [k, v.name]));

  const WORLD_TRACK_MAP = {
    home: "cosmic-explorer",
    learn: "enchanted-forest",
    arcade: "neon-rush",
    adventure: "ocean-voyage",
    art: "creative-canvas",
  };

  const playWorldMusic = useCallback((worldId) => {
    const track = WORLD_TRACK_MAP[worldId];
    if (track) playMusic(track);
  }, [playMusic]);

  return {
    soundEnabled, musicEnabled, volume, currentTrack,
    playSound, playMusic, stopMusic, toggleSound, toggleMusic,
    setVolume, nextTrack, trackList, trackNames, playWorldMusic,
  };
}
